import React from "react";
import { Box, Button, Chip, IconButton, Tooltip, Typography } from "@material-ui/core";
import { ArrowBack } from "@material-ui/icons";
import { css } from "@emotion/css";
import { useHistory, useParams } from "react-router-dom";
import { ErrorBox, Loading } from "shared";
import { Area } from "types";
import { Colors } from "theme";
import TextDiff from "routes/healthForms/components/TextDiff";
import {
  useDeploySchedule,
  useGetDeployableSchedule,
  useGetDeployedSchedule,
  useRevertToDeployed,
} from "routes/createContent/api/scheduleDeployments";
import { serializeSchedule } from "./serializeSchedule";

export const TemplateChangesPage: React.VFC = () => {
  const history = useHistory();
  const { id, group } = useParams<{ id: string; group: string }>();
  const scheduleId = parseInt(id, 10);
  const envGroup = group === "stage" || group === "prod" ? group : null;

  const deployedQuery = useGetDeployedSchedule(scheduleId, envGroup ?? "prod", envGroup !== null);
  const deployableQuery = useGetDeployableSchedule(scheduleId, envGroup !== null);
  const deploy = useDeploySchedule();
  const revert = useRevertToDeployed();

  const deployed = deployedQuery.data ?? null;
  const current = deployableQuery.data ?? null;
  const busy = deploy.isLoading || revert.isLoading;
  const back = () => history.push(Area.CreateContent_Templates);

  if (!envGroup) {
    return (
      <Typography variant="body2" color="textSecondary" className={styles.message}>
        Unknown environment group &quot;{group}&quot;.
      </Typography>
    );
  }

  if (deployedQuery.isLoading || deployableQuery.isLoading) {
    return <Loading />;
  }

  if (deployedQuery.isError || deployableQuery.isError) {
    return <ErrorBox error={deployedQuery.error ?? deployableQuery.error} verbose className={styles.message} />;
  }

  const previous = deployed ? serializeSchedule(deployed) : "";
  const next = current ? serializeSchedule(current) : "";
  const changed = previous !== next;

  return (
    <Box className={styles.page}>
      <Box className={styles.header}>
        <Tooltip title="Back to templates">
          <IconButton size="small" onClick={back} aria-label="Back">
            <ArrowBack />
          </IconButton>
        </Tooltip>
        <Typography variant="h5">{current?.key ?? deployed?.key ?? `Template ${scheduleId}`}</Typography>
        <Chip
          size="small"
          color={changed ? "primary" : "default"}
          label={changed ? `Not on ${envGroup}` : `Matches ${envGroup}`}
        />
        <Box flexGrow={1} />
        {changed && (
          <>
            <Button
              className={styles.discard}
              disabled={!deployed || busy}
              onClick={() => revert.mutate({ scheduleId, group: envGroup }, { onSuccess: back })}
            >
              Discard saved changes
            </Button>
            <Button
              color="primary"
              variant="contained"
              disabled={busy}
              onClick={() => deploy.mutate({ scheduleId, group: envGroup }, { onSuccess: back })}
            >
              Deploy to {envGroup}
            </Button>
          </>
        )}
      </Box>

      <Box className={styles.body}>
        {!deployed ? (
          <Typography variant="body2" color="textSecondary" className={styles.message}>
            Nothing is deployed to {envGroup} yet, so there is nothing to compare against.
          </Typography>
        ) : (
          <TextDiff previous={previous} next={next} previousLabel={`On ${envGroup}`} nextLabel="Saved" />
        )}
      </Box>
    </Box>
  );
};

const styles = {
  page: css`
    display: flex;
    flex-direction: column;
    height: calc(100vh - 90px);
    padding-right: 24px;
    box-sizing: border-box;
    gap: 16px;
  `,
  header: css`
    display: flex;
    align-items: center;
    gap: 12px;
  `,
  body: css`
    flex: 1;
    overflow: auto;
    border: 1px solid #e0e0e0;
    border-radius: 4px;
    background: #fff;
  `,
  message: css`
    padding: 16px;
  `,
  discard: css`
    && {
      color: ${Colors.ERROR};
    }
    &&.Mui-disabled {
      color: rgba(0, 0, 0, 0.26);
    }
  `,
};

export default TemplateChangesPage;
